(() => {
    "use strict";

    if (window.__PRODUCT_CARD_QUICK_ADD_BOOTED) {
        console.warn("[PRODUCT_CARD_QUICK_ADD] Already booted - skipping");
        return;
    }
    window.__PRODUCT_CARD_QUICK_ADD_BOOTED = true;

    /******************************************************************
     * CONFIG
     ******************************************************************/
    const NS = "PRODUCT_CARD_QUICK_ADD";
    const DEBUG = true;

    const CARD_IGNORE_SELECTORS = [
        ".carousel-controls",
        ".product_sizes",
        ".size-option",
        ".color-swatches",
        ".color-swatch",
        ".product_variants",
        ".product_variants_wrapper",
        "modal-opener",
        "modal-dialog",
        ".button-popup-option",
        "button",
        "a"
    ];

    // Cart section rendering fallback spec (update if your theme differs)
    const FALLBACK_RENDER_SPEC = [
        { section: "cart-drawer", selector: ".drawer__inner" },
        { section: "cart-icon-bubble", selector: "#cart-icon-bubble" }
    ];

    /******************************************************************
     * LOGGING
     ******************************************************************/
    function log(...args) {
        if (!DEBUG) return;
        console.log(`[${NS}]`, ...args);
    }
    function warn(...args) {
        console.warn(`[${NS}]`, ...args);
    }
    function err(...args) {
        console.error(`[${NS}]`, ...args);
    }

    /******************************************************************
     * SHOPIFY HELPERS
     ******************************************************************/
    function getShopifyRoot() {
        return window.Shopify?.routes?.root ? window.Shopify.routes.root : "/";
    }

    function sleep(ms) {
        return new Promise((r) => setTimeout(r, ms));
    }

    async function waitForSelector(selector, timeoutMs = 2500) {
        const start = Date.now();
        while (Date.now() - start < timeoutMs) {
            const el = document.querySelector(selector);
            if (el) return el;
            await sleep(50);
        }
        return null;
    }

    /******************************************************************
     * PRODUCT DATA PARSING (sizes)
     ******************************************************************/
    function splitProductRows(raw) {
        const rows = [];
        let buffer = "";

        String(raw)
          .split(",")
          .forEach((part) => {
              buffer = buffer ? `${buffer},${part}` : part;

              const fields = buffer.replace(/^"|"$/g, "").split("|");
              if (fields.length >= 4 && fields[3]?.trim()) {
                  rows.push(buffer.trim());
                  buffer = "";
              }
          });

        if (buffer.trim()) rows.push(buffer.trim());

        return rows.map((s) => s.trim()).filter(Boolean);
    }

    function getProductRows(productItem) {
        const raw = productItem?.dataset?.product;
        if (!raw) return [];

        // Try JSON forms first
        try {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed)) {
                return parsed.map(String).map((s) => s.trim()).filter(Boolean);
            }
            if (typeof parsed === "string") {
                return splitProductRows(parsed);
            }
        } catch (_) {}

        // Fallback: plain comma-separated string
        return splitProductRows(raw);
    }

    function getSizesForColor(productItem, selectedColor) {
        const rows = getProductRows(productItem);

        const fallbackColor = (rows[0] || "").split("|")[0];
        const color = selectedColor || productItem.dataset.defaultColor || fallbackColor;

        const chosen = String(color || "").trim().toLowerCase();

        const sizes = rows
          .map((row) => String(row).replace(/^"|"$/g, "").split("|"))
          .filter((parts) => parts.length >= 4)
          .filter((parts) => String(parts[0] || "").trim().toLowerCase() === chosen)
          .map((parts) => ({
              label: String(parts[1] || "").trim(),
              available: String(parts[2]).trim() === "true",
              variantId: String(parts[3] || "").trim()
          }));

        return { color, sizes, rowsCount: rows.length };
    }

    /******************************************************************
     * VIEW MODE (grid/list)
     ******************************************************************/
    function getViewMode() {
        const grid = document.getElementById("product-grid");
        return grid?.getAttribute("data-view-mode") || "grid";
    }

    function updateViewModeImages(productItem) {
        const container = productItem?.querySelector(".image-container");
        if (!container) return;

        const imgs = container.querySelectorAll(".product-img");
        if (!imgs.length) return;

        imgs.forEach((i) => i.classList.remove("visible"));

        const mode = getViewMode();
        if (mode === "list") imgs[imgs.length - 1].classList.add("visible");
        else imgs[0].classList.add("visible");
    }

    /******************************************************************
     * PRODUCT JSON + IMAGES (swatch updates)
     ******************************************************************/
    function uniq(arr) {
        const seen = new Set();
        return arr.filter((x) => x && !seen.has(x) && (seen.add(x), true));
    }

    function createMediaElement(item) {
        if (item.type === "video") {
            const video = document.createElement("video");
            video.classList.add("product-img", "product-video");
            video.autoplay = true;
            video.muted = true;
            video.loop = true;
            video.playsInline = true;
            video.preload = "auto";
            if (item.poster) video.poster = item.poster;

            (item.sources || []).forEach((sourceItem) => {
                if (!sourceItem?.url) return;
                const source = document.createElement("source");
                source.src = sourceItem.url;
                if (sourceItem.mime_type) source.type = sourceItem.mime_type;
                video.appendChild(source);
            });

            return video;
        }

        const img = document.createElement("img");
        img.src = item.src;
        img.loading = "lazy";
        img.classList.add("product-img");
        if (item.alt) img.alt = item.alt;
        return img;
    }

    async function getProductJson(handle) {
        window.__PRODUCT_CARD_QUICK_ADD_CACHE ||= new Map();
        const cache = window.__PRODUCT_CARD_QUICK_ADD_CACHE;

        if (cache.has(handle)) return cache.get(handle);

        const res = await fetch(`/products/${handle}.js`, { headers: { Accept: "application/json" } });
        if (!res.ok) throw new Error(`Product JSON fetch failed: ${res.status}`);
        const data = await res.json();
        cache.set(handle, data);
        return data;
    }

    async function updateImages(productItem, selectedColor, variantId = null) {
        const handle = productItem?.dataset?.handle;
        if (!handle) return;

        log("updateImages", { handle, selectedColor, variantId });

        try {
            const product = await getProductJson(handle);

            const media = Array.isArray(product.media) ? product.media : [];
            const mediaItems = media
              .filter((m) => m.media_type === "image" || m.media_type === "video")
              .map((m) => ({
                  type: m.media_type,
                  src: m.preview_image?.src,
                  poster: m.preview_image?.src,
                  alt: (m.alt || "").trim(),
                  sources: Array.isArray(m.sources) ? m.sources : []
              }))
              .filter((item) => item.src);

            const all = mediaItems;

            // 1) alt exact match with color (like your earlier logic)
            const wanted = String(selectedColor || "").trim().toLowerCase();
            const byAlt = mediaItems.filter((item) => item.alt.toLowerCase() === wanted);

            let finalList = byAlt.length ? byAlt : all;

            // 2) If no alt matches, put selected variant featured first
            if (!byAlt.length && variantId && Array.isArray(product.variants)) {
                const v = product.variants.find((v) => String(v.id) === String(variantId));
                const featured = v?.featured_image?.src;
                if (featured) {
                    finalList = [
                        {
                            type: "image",
                            src: featured,
                            poster: featured,
                            alt: ""
                        },
                        ...finalList
                    ];
                }
            }

            finalList = uniq(finalList.map((item) => `${item.type}:${item.src}`)).map((key) =>
              finalList.find((item) => `${item.type}:${item.src}` === key)
            );

            const container = productItem.querySelector(".image-container");
            if (!container) return;

            container.innerHTML = "";

            finalList.forEach((item) => {
                const mediaElement = createMediaElement(item);
                container.appendChild(mediaElement);
            });

            updateViewModeImages(productItem);
        } catch (e) {
            err("updateImages failed:", e);
        }
    }

    // Keep your Liquid arrows working (onclick="changeImage(...)")
    window.changeImage = function (button, direction) {
        const productImage = button.closest(".product_image");
        if (!productImage) return;

        const images = productImage.querySelectorAll(".product-img");
        if (!images.length) return;

        const currentIndex = [...images].findIndex((img) => img.classList.contains("visible"));
        const idx = currentIndex >= 0 ? currentIndex : 0;

        images[idx].classList.remove("visible");

        const nextIndex =
          direction === "prev"
            ? (idx - 1 + images.length) % images.length
            : (idx + 1) % images.length;

        images[nextIndex].classList.add("visible");
    };

    /******************************************************************
     * CART RENDER SPEC
     ******************************************************************/
    function getCartDrawer() {
        return document.querySelector("cart-drawer");
    }

    function clearDrawerEmptyClass() {
        const drawer = getCartDrawer();
        if (!drawer) return;

        // Remove empty-state classes that hide content
        drawer.classList.remove("is-empty");

        drawer
          .querySelectorAll(".drawer, .drawer__inner, .drawer__content, .cart-drawer, .cart-drawer__content")
          .forEach((el) => el.classList.remove("is-empty"));

        log("clearDrawerEmptyClass()");
    }

    function getCartRenderSpec() {
        const drawer = getCartDrawer();
        const list = drawer?.getSectionsToRender?.();
        if (Array.isArray(list) && list.length && list.every((x) => x?.section && x?.selector)) return list;
        return FALLBACK_RENDER_SPEC;
    }

    function getSectionsToRequest() {
        const spec = getCartRenderSpec();
        const sections = [...new Set(spec.map((s) => s.section).filter(Boolean))];
        log("sectionsToRequest:", sections, "renderSpec:", spec);
        return sections;
    }

    /******************************************************************
     * CART API (FULL DEBUG)
     ******************************************************************/
    async function fetchCart() {
        const root = getShopifyRoot();
        const url = `${root}cart.js`;
        log("GET", url);
        const res = await fetch(url, { headers: { Accept: "application/json" } });
        const json = await res.json();
        log("GET cart.js ->", res.status, { item_count: json?.item_count, items: json?.items?.length });
        return json;
    }

    async function fetchRenderedSections(sections) {
        const root = getShopifyRoot();
        const url = `${root}?sections=${sections.join(",")}`;
        log("GET sections:", url);

        const res = await fetch(url, { credentials: "same-origin" });
        const text = await res.text();

        try {
            const json = JSON.parse(text);
            log("sections JSON keys:", Object.keys(json || {}));
            return json && typeof json === "object" ? json : {};
        } catch (e) {
            warn("Sections endpoint returned non-JSON:", { status: res.status, head: text.slice(0, 120) });
            return {};
        }
    }

    async function cartAdd(variantId, quantity = 1) {
        const root = getShopifyRoot();
        const sections = getSectionsToRequest();
        const url = `${root}cart/add.js`;

        // Force sections_url to "/" for consistent rendering across templates
        const payload = {
            items: [{ id: Number(variantId), quantity: Number(quantity) }],
            sections,
            sections_url: "/"
        };

        log("POST", url, payload);

        const res = await fetch(url, {
            method: "POST",
            headers: { "Content-Type": "application/json", Accept: "application/json" },
            body: JSON.stringify(payload)
        });

        const body = await res.json().catch(() => ({}));
        log("cart/add.js ->", res.status, body);

        if (!res.ok) throw new Error(body?.description || body?.message || "Could not add this item to the cart.");

        const rendered = await fetchRenderedSections(sections);

        const cart = await fetchCart();
        cart.sections = rendered;
        return cart;
    }

    async function cartChange(lineKey, quantity) {
        const root = getShopifyRoot();
        const sections = getSectionsToRequest();
        const url = `${root}cart/change.js`;

        const payload = {
            id: lineKey,
            quantity,
            sections,
            sections_url: "/"
        };

        log("POST", url, payload);

        const res = await fetch(url, {
            method: "POST",
            headers: { "Content-Type": "application/json", Accept: "application/json" },
            body: JSON.stringify(payload)
        });

        const body = await res.json().catch(() => ({}));
        log("cart/change.js ->", res.status, body);

        if (!res.ok) throw new Error(body?.description || body?.message || "Could not update cart quantity.");

        const rendered = await fetchRenderedSections(sections);
        body.sections = rendered;
        return body;
    }

    async function upsertVariant(variantId) {
        const cart = await fetchCart();
        const existing = cart.items?.find((i) => String(i.id) === String(variantId));
        if (existing) {
            return cartChange(existing.key, (Number(existing.quantity) || 0) + 1);
        }
        return cartAdd(variantId, 1);
    }

    /******************************************************************
     * APPLY SECTIONS + OPEN DRAWER
     ******************************************************************/
    function applyCartSections(state) {
        const drawer = getCartDrawer();
        const sections = state?.sections;

        if (!sections) {
            warn("No sections on state; cannot render drawer.");
            return;
        }

        if (drawer && typeof drawer.renderContents === "function") {
            log("drawer.renderContents(state)");
            clearDrawerEmptyClass();
            drawer.renderContents(state);
            requestAnimationFrame(() => clearDrawerEmptyClass());
            return;
        }

        const spec = getCartRenderSpec();
        spec.forEach(({ section, selector }) => {
            const html = sections[section];
            if (!html) {
                warn("Missing HTML for section:", section, "available:", Object.keys(sections));
                return;
            }

            const doc = new DOMParser().parseFromString(html, "text/html");
            const src = doc.querySelector(selector);
            const dst = document.querySelector(selector);

            if (!src || !dst) {
                warn("Selector mismatch:", { section, selector, hasSrc: !!src, hasDst: !!dst });
                return;
            }

            dst.innerHTML = src.innerHTML;
        });
    }

    function updateHeaderCartCountFromState(state) {
        // count from state (prefer item_count)
        const count =
          typeof state?.item_count === "number"
            ? state.item_count
            : Array.isArray(state?.items)
              ? state.items.reduce((s, it) => s + (Number(it.quantity) || 0), 0)
              : 0;

        const cartLink = document.querySelector("#cart-icon-bubble");
        if (!cartLink) return;

        /***********************
         * DESKTOP: CART (x)
         ***********************/
        // Your markup has:
        // #cart-icon-bubble .cart-text .menu-text (top-text + bottom-text)
        document.querySelectorAll("#cart-icon-bubble .cart-text .menu-text").forEach((el) => {
            // Remove any existing "(number)" and append current count if > 0
            const base = el.textContent.replace(/\(\d+\)/g, "").replace(/\s+/g, " ").trim();
            el.textContent = count > 0 ? `${base} (${count})` : base;
        });

        /***********************
         * MOBILE: bubble number
         ***********************/
          // Liquid only renders the bubble when cart != empty.
          // If cart was empty, bubble doesn't exist -> we create it.
        let bubbleWrap = cartLink.querySelector(".cart-count-bubble");

        if (!bubbleWrap) {
            bubbleWrap = document.createElement("div");
            bubbleWrap.className = "cart-count-bubble large-up-hide";
            bubbleWrap.innerHTML = `
              <span aria-hidden="true"></span>
              <span class="visually-hidden"></span>
            `;

            // Prefer placing it right after the icon (so CSS positioning matches theme)
            const icon = cartLink.querySelector(".cart-icon");
            if (icon) icon.insertAdjacentElement("afterend", bubbleWrap);
            else cartLink.appendChild(bubbleWrap);
        }

        const bubbleNum = bubbleWrap.querySelector('span[aria-hidden="true"]');
        const bubbleHidden = bubbleWrap.querySelector(".visually-hidden");

        if (bubbleNum) bubbleNum.textContent = count > 0 ? String(count) : "";
        if (bubbleHidden) bubbleHidden.textContent = `Cart (${count})`;

        // Show/hide bubble depending on count
        bubbleWrap.style.display = count > 0 ? "" : "none";

        // Optional: if your theme toggles empty/full cart icons, keep current icon stable
        // (do nothing here to avoid desktop switching to icon-only)
    }


    async function openCartDrawer() {
        const drawer = await waitForSelector("cart-drawer", 2500);
        if (!drawer) {
            warn("cart-drawer not found, cannot open.");
            return;
        }
        if (typeof drawer.open === "function") {
            log("drawer.open()");
            drawer.open();
            return;
        }
        drawer.setAttribute("open", "");
        drawer.classList.add("is-open");
        log("drawer opened via attribute/class fallback");
    }

    /******************************************************************
     * UI: SIZES RENDER
     ******************************************************************/
    function renderSizes(productItem, selectedColor) {
        if (!productItem) return;

        const desktopSizes = productItem.querySelector(".product_image .product_sizes");

        const modalSelector = productItem.querySelector("modal-opener[data-modal]")?.dataset.modal;
        const modalEl = modalSelector ? document.querySelector(modalSelector) : null;
        const modalSizes = modalEl ? modalEl.querySelector(".product_sizes") : null;

        const targets = [desktopSizes, modalSizes].filter(Boolean);
        if (!targets.length) return;

        const { color, sizes, rowsCount } = getSizesForColor(productItem, selectedColor);

        log("renderSizes", {
            handle: productItem.dataset.handle,
            color,
            rowsCount,
            sizes
        });

        targets.forEach((container) => {
            container.innerHTML = "";

            sizes.forEach((s) => {
                const btn = document.createElement("button");
                btn.type = "button";
                btn.className = "size-option font-alt-heading small";
                btn.textContent = s.label;
                btn.disabled = !s.available;
                btn.dataset.variantId = s.variantId;
                container.appendChild(btn);
            });
        });

        productItem.dataset.currentColor = color;
    }

    function showError(productItem, modalEl, message) {
        if (modalEl) {
            let box = modalEl.querySelector(".size-modal-error");
            if (!box) {
                box = document.createElement("div");
                box.className = "size-modal-error font-alt-heading small";
                box.setAttribute("aria-live", "polite");
                box.style.marginTop = "10px";
                modalEl.querySelector(".global-modal__content-info")?.appendChild(box);
            }
            box.textContent = message;
            return;
        }

        err("SIZE ERROR:", message);
        const holder = productItem.querySelector(".product_image .product_sizes");
        if (holder) {
            let box = holder.querySelector(".size-inline-error");
            if (!box) {
                box = document.createElement("div");
                box.className = "size-inline-error";
                box.style.marginTop = "8px";
                box.style.fontSize = "12px";
                holder.appendChild(box);
            }
            box.textContent = message;
        }
    }

    function getProductItemForSizeButton(btn) {
        const inlineProductItem = btn.closest(".product_item");
        if (inlineProductItem) return inlineProductItem;

        const modalEl = btn.closest("modal-dialog.size-modal");
        if (!modalEl?.id) return null;

        const opener = [...document.querySelectorAll("modal-opener[data-modal]")]
          .find((el) => el.dataset.modal === `#${modalEl.id}`);

        return opener?.closest(".product_item") || null;
    }

    function getSizeModalForProductItem(productItem, btn) {
        const activeModal = btn.closest("modal-dialog.size-modal");
        if (activeModal) return activeModal;

        const modalSelector = productItem?.querySelector("modal-opener[data-modal]")?.dataset.modal;
        return modalSelector ? document.querySelector(modalSelector) : null;
    }

    /******************************************************************
     * CARD INIT
     ******************************************************************/
    function initCard(productItem) {
        if (!productItem || productItem.dataset.bound === "true") return;
        productItem.dataset.bound = "true";

        const defaultColor =
          productItem.dataset.defaultColor ||
          productItem.querySelector(".color-swatch.is-active")?.dataset.color;

        // sizes + images on first load
        renderSizes(productItem, defaultColor);

        // If the first load images were rendered by Liquid already, we leave them.
        // But we can "normalize" visibility for view mode:
        updateViewModeImages(productItem);

        log("initCard", {
            handle: productItem.dataset.handle,
            defaultColor,
            url: productItem.dataset.url
        });
    }

    function initAll(root = document) {
        root.querySelectorAll(".product_item").forEach(initCard);
    }

    /******************************************************************
     * EVENTS (DELEGATED)
     ******************************************************************/
    async function onSizeClick(e) {
        const btn = e.target.closest(".size-option");
        if (!btn) return;

        const productItem = getProductItemForSizeButton(btn);
        if (!productItem) return;

        e.preventDefault();
        e.stopPropagation();

        const variantId = btn.dataset.variantId;
        const modalEl = getSizeModalForProductItem(productItem, btn);

        log("SIZE CLICK", {
            handle: productItem?.dataset?.handle,
            variantId,
            disabled: btn.disabled,
            currentColor: productItem?.dataset?.currentColor,
            pathname: window.location.pathname
        });

        if (!variantId) {
            showError(productItem, modalEl, "Variant ID missing for this size.");
            return;
        }

        btn.disabled = true;

        try {
            const state = await upsertVariant(variantId);
            if (modalEl && typeof modalEl.hide === "function") modalEl.hide();
            applyCartSections(state);
            updateHeaderCartCountFromState(state);
            if (typeof publish === "function" && typeof PUB_SUB_EVENTS !== "undefined" && PUB_SUB_EVENTS?.cartUpdate) {
                publish(PUB_SUB_EVENTS.cartUpdate, {
                    source: "product-card-quick-add",
                    cartData: state,
                    variantId
                });
            }
            await openCartDrawer();
            btn.disabled = false;
        } catch (e2) {
            btn.disabled = false;
            showError(productItem, modalEl, e2?.message || String(e2));
        }
    }

    async function onSwatchClick(e) {
        const swatch = e.target.closest(".product_item .color-swatch");
        if (!swatch) return;

        e.preventDefault();
        e.stopPropagation();

        const productItem = swatch.closest(".product_item");

        productItem.querySelectorAll(".color-swatch").forEach((s) => s.classList.remove("is-active"));
        swatch.classList.add("is-active");

        const selectedColor = swatch.dataset.color;
        const variantId = swatch.dataset.variantId;

        log("SWATCH CLICK", {
            handle: productItem.dataset.handle,
            selectedColor,
            variantId
        });

        // Update card URL
        if (variantId) {
            const base =
              productItem.dataset.productUrlBase ||
              `${getShopifyRoot()}products/${productItem.dataset.handle}`;
            productItem.dataset.url = `${base}?variant=${variantId}`;
        }

        renderSizes(productItem, selectedColor);
        await updateImages(productItem, selectedColor, variantId);
    }

    function onCardClick(e) {
        const card = e.target.closest(".product_item");
        if (!card) return;

        const shouldIgnore = CARD_IGNORE_SELECTORS.some((sel) => e.target.closest(sel));
        if (shouldIgnore) return;

        const url = card.dataset.url;
        if (url) window.location.href = url;
    }

    function onModalOpenerClick(e) {
        const openerBtn = e.target.closest(".product_item .size-modal--menu-opener button");
        if (!openerBtn) return;

        const productItem = openerBtn.closest(".product_item");
        const color =
          productItem.querySelector(".color-swatch.is-active")?.dataset.color ||
          productItem.dataset.defaultColor;

        log("MODAL OPENER CLICK -> refresh sizes", { handle: productItem.dataset.handle, color });
        renderSizes(productItem, color);
    }

    // Optional: if you toggle view mode buttons, normalize visible image
    function onViewModeToggle(e) {
        const btn = e.target.closest(".model-toggle-button");
        if (!btn) return;

        // give your own toggle code time to set data-view-mode
        setTimeout(() => {
            document.querySelectorAll(".product_item").forEach(updateViewModeImages);
        }, 0);
    }

    /******************************************************************
     * OBSERVER (ONE ONLY)
     ******************************************************************/
    function installObserver() {
        if (window.__PRODUCT_CARD_QUICK_ADD_OBSERVER_INSTALLED) return;
        window.__PRODUCT_CARD_QUICK_ADD_OBSERVER_INSTALLED = true;

        const mo = new MutationObserver((mutations) => {
            for (const m of mutations) {
                for (const node of m.addedNodes) {
                    if (!(node instanceof Element)) continue;
                    if (node.matches(".product_item")) initCard(node);
                    else if (node.querySelector?.(".product_item")) initAll(node);
                }
            }
        });

        mo.observe(document.documentElement, { childList: true, subtree: true });
        log("MutationObserver installed");
    }

    /******************************************************************
     * BOOT
     ******************************************************************/
    function boot() {
        log("boot", { pathname: window.location.pathname });

        initAll();
        installObserver();

        document.addEventListener("click", onSizeClick, true);        // capture
        document.addEventListener("click", onSwatchClick, true);      // capture
        document.addEventListener("click", onModalOpenerClick, true); // capture
        document.addEventListener("click", onCardClick, false);       // bubble
        document.addEventListener("click", onViewModeToggle, true);   // capture

        // Warm up drawer + cart (helps first click reliability)
        waitForSelector("cart-drawer", 2500).then((d) => log("cart-drawer ready:", !!d));
        fetchCart().catch((e) => warn("cart warmup failed", e));
    }

    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", boot);
    } else {
        boot();
    }
})();
